import { useEffect, useMemo, useState } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { formatDistanceToNow } from 'date-fns';
import { Users, Eye, LinkIcon, FileCheck, RefreshCw, ArrowLeft, Bot, Building2, UserCheck } from 'lucide-react';

type Visitor = {
  visitor_id: string;
  first_seen_at: string;
  last_seen_at: string;
  identified_at: string | null;
  identified_via: 'link' | 'form' | null;
  identified_email: string | null;
  identified_name: string | null;
  identified_company: string | null;
  identified_contact_id: string | null;
  first_landing_page: string | null;
  first_referrer: string | null;
  first_utm_source: string | null;
  first_utm_campaign: string | null;
  pageview_count: number;
  visitor_type: 'human' | 'ai_bot' | 'unknown' | null;
  bot_name: string | null;
  user_agent: string | null;
  matched_org_id: string | null;
  matched_org_name: string | null;
  matched_user_id: string | null;
};

type Pageview = {
  id: number;
  visitor_id: string;
  path: string;
  referrer: string | null;
  viewed_at: string;
};

export default function AdminVisitors() {
  const { user, isAdmin, loading: authLoading } = useAuth();
  const [visitors, setVisitors] = useState<Visitor[]>([]);
  const [loading, setLoading] = useState(true);
  const [audience, setAudience] = useState<'humans' | 'ai'>('humans');
  const [filter, setFilter] = useState<'all' | 'identified' | 'anonymous'>('identified');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Visitor | null>(null);
  const [selectedPageviews, setSelectedPageviews] = useState<Pageview[]>([]);

  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    document.head.appendChild(meta);
    document.title = 'Visitors | NoQ5 Admin';
    return () => { document.head.removeChild(meta); };
  }, []);

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('noq5_visitors')
      .select('*')
      .order('last_seen_at', { ascending: false })
      .limit(500);
    if (!error && data) setVisitors(data as Visitor[]);
    setLoading(false);
  };

  useEffect(() => { if (isAdmin) load(); }, [isAdmin]);

  useEffect(() => {
    if (!selected) { setSelectedPageviews([]); return; }
    (async () => {
      const { data } = await supabase
        .from('noq5_pageviews')
        .select('id, visitor_id, path, referrer, viewed_at')
        .eq('visitor_id', selected.visitor_id)
        .order('viewed_at', { ascending: false })
        .limit(200);
      if (data) setSelectedPageviews(data as Pageview[]);
    })();
  }, [selected]);

  const audienceFiltered = useMemo(() => {
    return visitors.filter((v) => (audience === 'ai' ? v.visitor_type === 'ai_bot' : v.visitor_type !== 'ai_bot'));
  }, [visitors, audience]);

  const filtered = useMemo(() => {
    return audienceFiltered.filter((v) => {
      if (audience === 'humans') {
        if (filter === 'identified' && !v.identified_email && !v.identified_contact_id && !v.matched_user_id) return false;
        if (filter === 'anonymous' && (v.identified_email || v.identified_contact_id || v.matched_user_id)) return false;
      }
      if (search) {
        const q = search.toLowerCase();
        return (
          v.identified_email?.toLowerCase().includes(q) ||
          v.identified_company?.toLowerCase().includes(q) ||
          v.identified_name?.toLowerCase().includes(q) ||
          v.matched_org_name?.toLowerCase().includes(q) ||
          v.bot_name?.toLowerCase().includes(q) ||
          v.first_landing_page?.toLowerCase().includes(q)
        );
      }
      return true;
    });
  }, [audienceFiltered, audience, filter, search]);

  const stats = useMemo(() => {
    const humans = visitors.filter((v) => v.visitor_type !== 'ai_bot');
    const bots = visitors.filter((v) => v.visitor_type === 'ai_bot');
    return {
      humans: humans.length,
      identified: humans.filter((v) => v.identified_email || v.identified_contact_id).length,
      matchedOrg: humans.filter((v) => v.matched_org_id).length,
      aiBots: bots.length,
    };
  }, [visitors]);

  if (authLoading) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading…</div>;
  if (!user) return <Navigate to="/admin/login" replace />;
  if (!isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card><CardContent className="pt-6"><p className="text-destructive">Access denied. Admin privileges required.</p></CardContent></Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="container py-8 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <Link to="/admin/dashboard" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-2">
              <ArrowLeft className="w-3.5 h-3.5" /> Admin
            </Link>
            <h1 className="text-3xl font-bold tracking-tight">Visitors</h1>
            <p className="text-sm text-muted-foreground mt-1">Consent-gated tracking. Last 500 visitors.</p>
          </div>
          <Button variant="outline" size="sm" onClick={load} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard icon={Users} label="Human visitors" value={stats.humans} />
          <StatCard icon={Eye} label="Identified" value={stats.identified} accent />
          <StatCard icon={Building2} label="Matched to org" value={stats.matchedOrg} />
          <StatCard icon={Bot} label="AI crawlers" value={stats.aiBots} />
        </div>

        <div className="flex items-center gap-2 border-b border-border pb-0">
          {(['humans', 'ai'] as const).map((a) => (
            <button
              key={a}
              onClick={() => setAudience(a)}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${audience === a ? 'border-primary text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
            >
              {a === 'humans' ? <span className="inline-flex items-center gap-1.5"><Users className="w-3.5 h-3.5" /> Humans</span> : <span className="inline-flex items-center gap-1.5"><Bot className="w-3.5 h-3.5" /> AI crawlers</span>}
            </button>
          ))}
        </div>

        <Card>
          <CardHeader className="pb-3">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <div className="flex items-center gap-2">
                {audience === 'humans' && (['identified', 'all', 'anonymous'] as const).map((f) => (
                  <Button key={f} variant={filter === f ? 'default' : 'outline'} size="sm" onClick={() => setFilter(f)}>
                    {f[0].toUpperCase() + f.slice(1)}
                  </Button>
                ))}
              </div>
              <Input placeholder={audience === 'humans' ? 'Search email, org, page…' : 'Search bot, page…'} value={search} onChange={(e) => setSearch(e.target.value)} className="md:w-72" />
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{audience === 'humans' ? 'Visitor' : 'Crawler'}</TableHead>
                  <TableHead>{audience === 'humans' ? 'Attribution' : 'User agent'}</TableHead>
                  <TableHead>First touch</TableHead>
                  <TableHead className="text-right">Views</TableHead>
                  <TableHead>Last seen</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.length === 0 ? (
                  <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                    {audience === 'humans' ? 'No human visitors yet — the tracker only fires after consent.' : 'No AI crawlers observed yet.'}
                  </TableCell></TableRow>
                ) : filtered.map((v) => (
                  <TableRow key={v.visitor_id} className="cursor-pointer" onClick={() => setSelected(v)}>
                    <TableCell>
                      {audience === 'ai' ? (
                        <div>
                          <div className="font-medium text-foreground inline-flex items-center gap-1.5"><Bot className="w-3.5 h-3.5 text-primary" /> {v.bot_name ?? 'Unknown bot'}</div>
                          <div className="text-xs text-muted-foreground font-mono">{v.visitor_id.slice(0, 8)}</div>
                        </div>
                      ) : v.identified_email ? (
                        <div>
                          <div className="font-medium text-foreground">{v.identified_name ?? v.identified_email}</div>
                          <div className="text-xs text-muted-foreground">{v.identified_email}{v.identified_company ? ` · ${v.identified_company}` : ''}</div>
                        </div>
                      ) : v.identified_contact_id ? (
                        <Badge variant="secondary">Linked contact · {v.identified_contact_id.slice(0, 10)}…</Badge>
                      ) : (
                        <span className="text-muted-foreground text-sm">Anonymous · {v.visitor_id.slice(0, 8)}</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {audience === 'ai' ? (
                        <span className="text-xs text-muted-foreground font-mono truncate block max-w-xs">{v.user_agent ?? '—'}</span>
                      ) : (
                        <div className="space-y-0.5">
                          {v.matched_org_name && (
                            <div className="text-xs inline-flex items-center gap-1"><Building2 className="w-3 h-3 text-primary" /> <span className="font-medium">{v.matched_org_name}</span></div>
                          )}
                          {v.matched_user_id && (
                            <div className="text-xs inline-flex items-center gap-1 text-muted-foreground"><UserCheck className="w-3 h-3" /> Signed-in user</div>
                          )}
                          {!v.matched_org_name && !v.matched_user_id && <span className="text-xs text-muted-foreground">—</span>}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      <div className="truncate max-w-xs">{v.first_landing_page ?? '—'}</div>
                      {v.first_utm_source && <div className="text-xs">utm: {v.first_utm_source}{v.first_utm_campaign ? ` / ${v.first_utm_campaign}` : ''}</div>}
                    </TableCell>
                    <TableCell className="text-right font-mono text-sm">{v.pageview_count}</TableCell>
                    <TableCell className="text-sm text-muted-foreground whitespace-nowrap">{formatDistanceToNow(new Date(v.last_seen_at), { addSuffix: true })}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>


        {selected && (
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-lg">Visitor timeline</CardTitle>
                <p className="text-sm text-muted-foreground">
                  {selected.identified_email ?? `Anonymous · ${selected.visitor_id.slice(0, 8)}`}
                  {selected.first_referrer ? ` · from ${selected.first_referrer}` : ''}
                </p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setSelected(null)}>Close</Button>
            </CardHeader>
            <CardContent>
              <div className="space-y-1 max-h-96 overflow-y-auto">
                {selectedPageviews.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No pageviews yet.</p>
                ) : selectedPageviews.map((pv) => (
                  <div key={pv.id} className="flex justify-between items-center py-2 border-b border-border last:border-0">
                    <span className="text-sm font-mono text-foreground">{pv.path}</span>
                    <span className="text-xs text-muted-foreground">{formatDistanceToNow(new Date(pv.viewed_at), { addSuffix: true })}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, accent }: { icon: React.ElementType; label: string; value: number; accent?: boolean }) {
  return (
    <Card className={accent ? 'border-primary/40' : ''}>
      <CardContent className="pt-5 pb-4">
        <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase tracking-wider mb-1">
          <Icon className="w-3.5 h-3.5" />
          {label}
        </div>
        <div className={`text-2xl font-bold ${accent ? 'text-primary' : 'text-foreground'}`}>{value}</div>
      </CardContent>
    </Card>
  );
}
